<script lang="ts" setup>
	import { useProductsApi, type BenefitGroupedData } from '~/services/apis/products'

	const props = defineProps<{
		planDetails: PlanInformationContext | null
	}>()
	const activeTab = shallowRef<steps>('benefits')
	const { productCategoryType, toLower } = useUtils()
	const planCategory = computed(() => toLower(props.planDetails?.product?.productCategory.name ?? ''))
	const productCode = computed(() => props.planDetails?.product.code_name ?? 0)
	const provider = computed(() => props.planDetails?.product.underwriter ?? '')
	const category = computed(() =>
		productCategoryType(
			props.planDetails?.product?.productCategory.name,
			props.planDetails?.product?.code_name ?? ''
		)
	)

	const { getProductBenefit, getProductClaimProcess, getProductNeedToKnow, getProductWorks } = useProductsApi()
	const benefits = ref<Record<steps, { label: string; key: steps; list: Content; disabled?: boolean }>>({
		benefits: {
			label: 'Benefits',
			key: 'benefits',
			list: []
		},
		needToKnow: {
			label: 'Need To Know',
			key: 'needToKnow',
			list: []
		},
		howItWorks: {
			label: 'How It Works',
			key: 'howItWorks',
			list: [] as Content
		},
		howToClaim: {
			label: 'How To Claim',
			key: 'howToClaim',
			disabled: category.value?.isHealth || category.value?.isTravel,
			list: []
		}
	})
	const tabCollection = computed<lineTabContext[]>(() => {
		// Sort the list so hospital can be at the center if health
		return [
			...Object.values(benefits.value).map((item, i) => ({
				key: item.key,
				name: item.label,
				disabled: item.disabled,
				order: i === 0 ? i + 1 : i + 2
			})),
			{
				key: 'hospital_list',
				name: 'Hospital List',
				disabled: !category.value?.isHealth,
				order: 2
			}
		].sort((a, b) => {
			return b.order < a.order ? 1 : -1
		})
	})
	type Content = BenefitGroupedData[string]
	type steps = 'benefits' | 'needToKnow' | 'howItWorks' | 'howToClaim'

	const currentBenefit = computed(() => benefits.value[activeTab.value])
	const activeBenefit = shallowRef<string | null>(null)
	const loadingBenefit = shallowRef(false)
	const fetchBenefit = () => {
		benefits.value.howToClaim.disabled = category.value?.isHealth || category.value?.isTravel
		if (planCategory.value) {
			resolve(async () => {
				const options = {
					code_name: productCode.value,
					provider: provider.value,
					category: planCategory.value,
					user_type: UserTypes.BUSINESS
				}
				const [benefit, needToKnow, works] = await Promise.allSettled([
					getProductBenefit({ ...options, params: productCode.value }),
					getProductNeedToKnow(options),
					getProductWorks(options)
				])
				if (
					benefit.status === 'fulfilled' &&
					needToKnow.status === 'fulfilled' &&
					works.status === 'fulfilled'
				) {
					benefits.value.benefits.list = benefit.value as Content
					benefits.value.needToKnow.list = needToKnow.value as Content
					benefits.value.howItWorks.list = works.value as Content
					if (!category.value?.isHealth && !category.value?.isTravel) {
						const claim = await getProductClaimProcess(options)
						if (claim.length) {
							benefits.value.howToClaim.list = claim as Content
						}
					}
				}
			}, loadingBenefit)
		}
	}

	watch(() => props.planDetails, fetchBenefit, { immediate: true })
</script>

<template>
	<div>
		<tabs-pill
			v-model:active-tab="activeTab"
			:tabs="tabCollection"
			:use-default-wrapper-class="false"
			active-tab-class="bg-primary-2 "
			btn-wrapper="w-full flex items-center"
			body-class="border min-h-[400px]  w-full py-5 sm:px-5 px-5"
			:ui="{
				tab: {
					width: 'w-full',
					inactive: { color: 'bg-gray-100 text-gray-400' },
					rounded: 'rounded-none',
					base: ''
				},
				wrapper: { width: 'w-full' }
			}"
		>
			<template #default="{ key }">
				<my-cover-health-hospital-list
					v-if="key === 'hospital_list'"
					class="mt-5"
					:product-id="props.planDetails?.product.id || ''"
				/>
				<div class="md:px- main-content-box relative max-h-[700px] space-y-3 overflow-y-auto pb-4">
					<loader-skeleton v-if="loadingBenefit" :count="5" :height="50" class="w-full" :max-width="false" />
					<template v-for="(item, i) in currentBenefit.list" :key="'content' + i">
						<div
							v-if="(item.provider === provider || !category?.isHealth) && activeTab === 'benefits'"
							class=""
						>
							<div
								class="sticky left-0 top-0 inline-flex w-full items-center justify-between rounded-md bg-primary-25 px-2 py-4 font-medium capitalize"
							>
								<span class="font-semibold text-gray-600">{{ item?.title ?? '' }}</span>
								<button
									type="button"
									aria-label="toggle"
									:aria-expanded="
										activeBenefit === `${item.title}-${i}` || (i == 0 && activeBenefit === null)
									"
									:style="{
										transform:
											activeBenefit === `${item.title}-${i}` || (i == 0 && activeBenefit === null)
												? 'rotate(180deg)'
												: 'rotate(0deg)'
									}"
									class="custom-transition-2"
									@click="
										() =>
											(activeBenefit =
												activeBenefit === `${item.title}-${i}` ? '' : `${item.title}-${i}`)
									"
								>
									<tools-icon dir="arrows" icon="caret-down-r" />
								</button>
							</div>

							<div
								class="custom-transition-2 overflow-hidden overflow-y-auto"
								:style="{
									maxHeight:
										activeBenefit === `${item.title}-${i}` || (i == 0 && activeBenefit === null)
											? '400px'
											: '0'
								}"
							>
								<template v-for="(list, listIndex) in item.list" :key="listIndex">
									<div
										v-if="!['n/a', 'nil', ''].includes(String(list.label?.text).toLowerCase())"
										class="border-b-[2px] border-dotted border-gray-200"
									>
										<div class="flex items-start justify-between gap-x-1 pt-5 tracking-wide">
											<div class="flex-none pt-1">
												<lazy-tools-icon icon="check" dir="fill" />
											</div>
											<div class="flex w-full items-center justify-between">
												<div class="text-sm font-medium text-gray-700">
													{{ list.label?.text ?? '---' }}
												</div>
											</div>
										</div>
										<!-- Collection -->
										<ul v-if="list.collection?.length" class="m-0 px-5 py-1">
											<li
												v-for="(collection, collectionIndex) in list.collection"
												:key="collectionIndex"
												class="list-item w-full text-xs tracking-wider text-gray-600"
											>
												<div>{{ collection?.text ?? '' }}</div>
											</li>
										</ul>
										<div
											v-else
											class="list-item w-full px-5 py-1 text-xs tracking-wider text-gray-600"
										>
											<div>{{ list.description?.text || '' }}</div>
										</div>
									</div>
								</template>
							</div>
						</div>

						<div
							v-else-if="typeof item?.title != 'string' && item?.title?.text"
							class="w-full text-gray-600"
						>
							<div class="flex items-center gap-3 text-sm font-medium capitalize">
								<lazy-tools-icon icon="check" dir="fill" />
								<span class="text-gray-600">{{ item?.title?.text ?? '' }}</span>
							</div>
						</div>
					</template>
				</div>
			</template>
		</tabs-pill>
	</div>
</template>
